import { TFunction } from 'i18next';
import { isEmpty, maxBy } from 'lodash-es';

import { TableFilter } from '../../components/Table/components/filter/TableFilters';
import { TagSelectItem } from '../../components/TagSelect/TagSelect';
import { PurchaseOrderStatus, PurchaseOrdersDTO, PurchaseOrderType, Restriction, SearchType, SortDirection, PurchaseOrderExtraStatus, SupplierDTO } from '../../services/types/ApiTypes';
import { StampType } from '../../components/StampLabel/StampLabel';
import { formatDate } from '../../common/utils/formatters';

import api from '../../services/ApiServices';

import { POSearchParams } from './PurchaseOrdersViewReducer';

export const DEFAULT_RESTRICTION = 'GeneralSearch';
export const DATE_RESTRICTION = 'DateCreatedRange';
export const DEFAULT_SORT_COLUMN = 'OrderNumber';

export const getColumnName = (columnName: string) => (columnName === 'SupplierId' ? 'Supplier.Name' : columnName);

export function getPurchaseOrdersColumnName(columnName: string, translate?: TFunction): string {
    const columnNames = {
        OrderNumber: 'view.PurchaseOrders.Number',
        Supplier: 'view.PurchaseOrders.Supplier',
        SupplierId: 'view.PurchaseOrders.Supplier',
        SupplierName: 'view.PurchaseOrders.Supplier',
        ['Supplier.Name']: 'view.PurchaseOrders.Supplier',
        Description: 'view.PurchaseOrders.Description',
        DateCreated: 'view.PurchaseOrders.DateCreated',
        OrderStatus: 'view.PurchaseOrders.Status',
        OrderType: 'view.PurchaseOrders.Type',
        NetTotal: 'view.PurchaseOrders.Rows.NetTotal',
        Total: 'view.PurchaseOrders.SumTotal',
    };
    if (translate) {
        return translate(columnNames[columnName]);
    }
    return columnNames[columnName];
}

export function createRequest(searchValue = '', page = 1, count = 15, columnName = DEFAULT_SORT_COLUMN) {
    const search: POSearchParams = {
        SortItems: [
            {
                SortColumn: columnName,
                SortDirection: SortDirection.Desc,
            },
        ],
        PagingOptions: {
            Count: count,
            Page: page,
        },
        Restrictions: [
            {
                Field: DEFAULT_RESTRICTION,
                Value: searchValue || '',
                Values: null,
                FieldSearchType: SearchType.NotSelected,
            },
        ],
        filters: {},
    };
    return search;
}

export function getStatusKey(status: PurchaseOrderStatus) {
    const statusMap = {
        [PurchaseOrderStatus.New]: 'view.PurchaseOrders.Status.New',
        [PurchaseOrderStatus.Assigned]: 'view.PurchaseOrders.Status.Assigned',
        [PurchaseOrderStatus.Confirmed]: 'view.PurchaseOrders.Status.Confirmed',
        [PurchaseOrderStatus.Rejected]: 'view.PurchaseOrders.Status.Rejected',
        [PurchaseOrderStatus.Deleted]: 'view.PurchaseOrders.Status.Deleted',
    };
    return statusMap[status];
}

export function getStatuses() {
    return {
        [PurchaseOrderStatus.New]: 'view.PurchaseOrders.Status.New',
        [PurchaseOrderStatus.Assigned]: 'view.PurchaseOrders.Status.Assigned',
        [PurchaseOrderStatus.Confirmed]: 'view.PurchaseOrders.Status.Confirmed',
        [PurchaseOrderStatus.Rejected]: 'view.PurchaseOrders.Status.Rejected',
        [PurchaseOrderStatus.Deleted]: 'view.PurchaseOrders.Status.Deleted',
    };
}

export const getStatusStamptype = (status: PurchaseOrderStatus): StampType => {
    switch (status) {
        case PurchaseOrderStatus.New:
            return StampType.INFO;
        case PurchaseOrderStatus.Assigned:
            return StampType.ACTIVE;
        case PurchaseOrderStatus.Confirmed:
            return StampType.APPROVED;
        case PurchaseOrderStatus.Rejected:
            return StampType.ALARM;
        default:
            return StampType.NEUTRAL;
    }
};

export const getSupplierList = async (name: string, currenSupplier?: SupplierDTO) => {
    if (name) {
        const response = await api.suppliers.getSuppliersByName(name);

        if (currenSupplier) {
            const currentSupplierIndex = response?.data?.findIndex((e) => e.Id === currenSupplier.Id);
            if (currentSupplierIndex !== -1) {
                response?.data?.unshift(response?.data?.splice(currentSupplierIndex, 1)[0]);
            }
        }

        return response.data.map(
            (supplier): TagSelectItem<number> => {
                return {
                    value: supplier.Id,
                    text: `${supplier.Name} (${supplier.RegistrationCode})`,
                };
            },
        );
    }
    const response = await api.suppliers.getSuppliers({
        SortItems: [
            {
                SortDirection: SortDirection.Asc,
                SortColumn: 'Name',
            },
        ],
        Restrictions: [],
        PagingOptions: {
            Page: 1,
            Count: 25,
        },
    });
    //display current invoice supplier on top of the list
    if (currenSupplier) {
        const currentSupplierIndex = response?.data?.Items?.findIndex((e) => e.Id === currenSupplier.Id);
        if (currentSupplierIndex !== -1) {
            response?.data?.Items.unshift(response?.data?.Items?.splice(currentSupplierIndex, 1)[0]);
        } else {
            response.data.Items = [currenSupplier, ...response?.data?.Items];
        }
    }
    return Promise.resolve(
        response.data.Items.map(
            (supplier): TagSelectItem<number> => {
                return {
                    value: supplier.Id,
                    text: `${supplier.Name} (${supplier.RegistrationCode})`,
                };
            },
        ),
    );
};

export const getPurchaseOrderExtraStatusOptions = (): Array<TagSelectItem<any>> => {
    const purchaseExtraStatusArray = Object.keys(PurchaseOrderExtraStatus);
    return purchaseExtraStatusArray
        .filter((key) => isNaN(Number(PurchaseOrderExtraStatus[key])))
        .map((k, index) => ({
            value: Number(k),
            text: purchaseExtraStatusArray[index + purchaseExtraStatusArray.length / 2],
        }));
};

export const getStatusItemsArray = (t: TFunction): Array<TagSelectItem<any>> => {
    const statuses = getStatuses();
    return Object.keys(statuses).map((key) => {
        const parent = {
            text: t(statuses[key]),
            value: key,
        };
        return {
            ...parent,
            children:
                key === PurchaseOrderStatus.Confirmed.toString()
                    ? getPurchaseOrderExtraStatusOptions().map((k) => ({
                          ...k,
                          text: t(`view.PurchaseOrders.ExtraStatus.${k.text}`),
                          parent,
                      }))
                    : null,
        };
    });
};

export const parseRestrictions = (searchParams: POSearchParams, translate: TFunction) => {
    const appliedFilters: Array<TableFilter<any>> = [];
    searchParams.Restrictions.forEach((restriction) => {
        if (restriction.Field !== DEFAULT_RESTRICTION && searchParams.filters[restriction.Field]) {
            const newAppliedFilter: TableFilter<any> = {
                label: getPurchaseOrdersColumnName(restriction.Field, translate),
                columnName: restriction.Field,
                items: searchParams.filters[restriction.Field].items,
                loadItems: searchParams.filters[restriction.Field].loadItems,
                values: searchParams.filters[restriction.Field].values,
                tagSelectType: searchParams.filters[restriction.Field].tagSelectType,
                onSelectChangeCallback: searchParams.filters[restriction.Field].onSelectChangeCallback,
                placeholder: searchParams.filters[restriction.Field].placeholder,
                searchOnFocus: searchParams.filters[restriction.Field].searchOnFocus,
                childColumnName: searchParams.filters[restriction.Field]?.childColumnName,
            };
            if (!!newAppliedFilter.values && !isEmpty(newAppliedFilter.values)) {
                appliedFilters.push(newAppliedFilter);
            }
        }
    });
    return appliedFilters;
};

export const parseFilters = (filter: TableFilter<any>): Restriction => {
    const appliedFilter: Restriction = {
        Field: filter.columnName,
        Value: null,
        Values: null,
        FieldSearchType: SearchType.NotSelected,
    };
    if (filter && filter.values.length > 1) {
        appliedFilter.Value = null;
        appliedFilter.Values = filter.values.map((val) => val.value);
    }
    if (filter && filter.values.length === 1) {
        appliedFilter.Values = null;
        appliedFilter.Value = filter.values[0].value;
    }
    return appliedFilter;
};

export const getTypeKey = (status: PurchaseOrderType) => {
    const statusMap = {
        [PurchaseOrderType.Capex]: 'view.PurchaseOrders.Type.Capex',
        [PurchaseOrderType.Opex]: 'view.PurchaseOrders.Type.Opex',
        [PurchaseOrderType.Unknown]: 'view.PurchaseOrders.Type.Unknown',
    };
    return statusMap[status];
};

export const getStatusData = (po: PurchaseOrdersDTO) => {
    const assignees = po.CurrentlyAssignedUsers ? po.CurrentlyAssignedUsers.map((u) => u.FirstName + ' ' + u.LastName).join(', ') : '';
    const statusDataMap = {
        [PurchaseOrderStatus.New]: '',
        [PurchaseOrderStatus.Assigned]: `(${assignees})`,
        [PurchaseOrderStatus.Rejected]: `(${formatDate(maxBy(po.PurchaseOrderTasks, (taskItem) => taskItem?.OrderNo)?.ModifiedDate || po.DateCreated)})`, // should return po.RejectedDate, but this data does not exist yet
        [PurchaseOrderStatus.Confirmed]: `(${formatDate(maxBy(po.PurchaseOrderTasks, (taskItem) => taskItem?.OrderNo)?.ModifiedDate || po.DateCreated)})`, // should return po.ApprovedDate, but this data does not exist yet
        [PurchaseOrderStatus.Deleted]: `(${formatDate(maxBy(po.PurchaseOrderTasks, (taskItem) => taskItem?.OrderNo)?.ModifiedDate || po.DateCreated)})`, // should return po.DeletedDate, but this data does not exist yet
    };
    return statusDataMap[po.OrderStatus];
};
